import {
    sendPasswordlessEmail,
    sendSoftAccountActivationEmail,
    sendVerificationEmail,
} from '../api/emails';
import {
    GA_EVENTBRITE,
    GA_SOFT_ACCOUNT_MAIL_FAILED,
    GA_SOFT_ACCOUNT_MAIL_SENT,
} from '../constants/analytics';
import { IAllContextsProps } from '../constants/interfaces';
import { LOGIN_SOFT_ACCOUNT_MESSAGE } from '../constants/loginMessages';
import { NOT_EMAIL_ASSOCIATED_MESSAGE } from '../constants/lookupMessages';
import {
    CREATE_ACCOUNT_MESSAGE,
    GENERIC_ERROR_MESSAGE,
} from '../constants/messages';
import {
    NOT_AUTHORIZED_ERROR_MESSAGE,
    SUSPENDED_ACCOUNT_ERROR_MESSAGE,
} from '../constants/passwordlessMessages';
import {
    PASSWORDLESS_EMAIL_STATUSES,
    SOFT_ACCOUNT_EMAIL_STATUSES,
    VERIFICATION_EMAIL_STATUSES,
    VERIFICATION_STATUSES,
} from '../constants/status';
import { MODAL_TYPES } from '../contexts/FrontendContext';
import { trackGeneralAuthAction } from '../tracking/general';
import {
    trackPasswordlessEmailAttempt,
    trackPasswordlessEmailFailed,
    trackPasswordlessEmailSent,
} from '../tracking/passwordless';
import { trackSocialAction } from '../tracking/social';
import {
    trackVerificationEmailAttempt,
    trackVerificationEmailFailed,
    trackVerificationEmailSent,
} from '../tracking/verification';

/**  ACTIONS  **/
/**
 * Sends email activation for Soft Account
 */
export const sendSoftAccountEmailAction = ({
    contexts,
}: {
    contexts: IAllContextsProps;
}) => {
    const email = contexts.userContext?.state.lookup.lookedAccount
        ? contexts.userContext?.state.lookup.lookedAccount
        : '';
    const socialProvider = contexts.userContext?.state.socialProvider
        ? contexts.userContext?.state.socialProvider
        : GA_EVENTBRITE;
    const referrer = contexts.configContext?.referrer;

    contexts.notificationsContext?.methods.setGeneralNotification({});
    contexts.frontendContext?.methods.setSoftAccountEmailStatus(
        SOFT_ACCOUNT_EMAIL_STATUSES.ongoing,
    );

    return sendSoftAccountActivationEmail({ email, destination: referrer })
        .then(() => {
            contexts.frontendContext?.methods.setSoftAccountEmailStatus(
                SOFT_ACCOUNT_EMAIL_STATUSES.delivered,
            );

            contexts.notificationsContext?.methods.setGeneralNotification({
                message: LOGIN_SOFT_ACCOUNT_MESSAGE.replace('{email}', email),
            });

            trackSocialAction({
                action: GA_SOFT_ACCOUNT_MAIL_SENT,
                socialProvider,
                analytics: contexts.configContext?.analytics,
            });
        })
        .catch((error: any) => {
            contexts.frontendContext?.methods.setSoftAccountEmailStatus(
                SOFT_ACCOUNT_EMAIL_STATUSES.failed,
            );

            // Local notification?
            contexts.notificationsContext?.methods.setGeneralNotification({
                message: GENERIC_ERROR_MESSAGE,
            });

            const errorMessage =
                error?.response?.parsedError?.error ??
                error?.response?.statusText ??
                'Error not defined';

            trackGeneralAuthAction({
                action: GA_SOFT_ACCOUNT_MAIL_FAILED,
                label: errorMessage,
                analytics: contexts.configContext?.analytics,
            });
        });
};

/**
 * Sends passworless login email
 */
export const sendPasswordlessEmailAction = ({
    email,
    isDirect = false,
    contexts,
}: {
    email: string;
    isDirect?: boolean;
    contexts: IAllContextsProps;
}) => {
    trackPasswordlessEmailAttempt({
        socialProvider: GA_EVENTBRITE,
        analytics: contexts.configContext?.analytics,
    });
    const setGeneralNotification =
        contexts.notificationsContext?.methods.setGeneralNotification;
    const setPasswordlessEmailStatus =
        contexts.frontendContext?.methods.setPasswordlessEmailStatus;

    setGeneralNotification?.({});
    setPasswordlessEmailStatus?.(PASSWORDLESS_EMAIL_STATUSES.ongoing);
    contexts.frontendContext?.methods.setSignupSecondStep(false);
    contexts.userContext?.methods.lookup.setExistentAccount('');

    return sendPasswordlessEmail(email)
        .then(() => {
            setPasswordlessEmailStatus?.(PASSWORDLESS_EMAIL_STATUSES.delivered);
            trackPasswordlessEmailSent({
                socialProvider: GA_EVENTBRITE,
                analytics: contexts.configContext?.analytics,
            });
        })
        .catch((error: any) => {
            const errorCode =
                error?.parsedError?.error ??
                error?.response?.statusText ??
                'Error not defined';
            setPasswordlessEmailStatus?.(PASSWORDLESS_EMAIL_STATUSES.failed);

            switch (errorCode) {
                case 'USER_SUSPENDED':
                    setGeneralNotification?.({
                        message: SUSPENDED_ACCOUNT_ERROR_MESSAGE,
                    });
                    break;
                // TODO: complete when having the modal component and the button
                case 'INVALID':
                    // let iconType = null;
                    if (isDirect) {
                        // If user entered email in login form, then hide modal
                        contexts.frontendContext?.methods.setShowModal(
                            MODAL_TYPES.closed,
                        );
                    } else {
                        // Display error on modal
                        // dispatch(
                        //     updateSyncErrors(
                        //         PASSWORDLESS_FORM,
                        //         PASSWORDLESS_ERROR_SPEC['EMAIL_CREATE_ACCOUNT'],
                        //     ),
                        // );
                        // dispatch(touch(PASSWORDLESS_FORM, 'email'));
                        // iconType = <AlertChunky />;
                        // formikActions?.setFieldTouched(PASSWORD_FIELD, true);
                        // formikActions?.setFieldError(PASSWORD_FIELD, ACCESS_DENIED_MESSAGE);
                    }

                    const passwordlessEmailInvalidNotification = contexts.configContext?.passwordlessMethods?.sendEmail?.error?.INVALID.getNotification?.();

                    setGeneralNotification?.({
                        message: NOT_EMAIL_ASSOCIATED_MESSAGE,

                        extraMessage:
                            passwordlessEmailInvalidNotification?.extraMessage,
                        cta: {
                            message:
                                passwordlessEmailInvalidNotification?.ctaMessage ??
                                CREATE_ACCOUNT_MESSAGE,
                            action:
                                passwordlessEmailInvalidNotification?.ctaAction,
                        },
                        // iconType,
                    });
                    // dispatch(change(LOG_IN_FORM, 'email', email));

                    // formikActions?.setValues('email', email);
                    contexts.frontendContext?.methods.setSignupSecondStep(true);

                    contexts.userContext?.methods.lookup.setExistentAccount(
                        email,
                    );
                    break;
                case 'NOT_AUTHORIZED':
                    setGeneralNotification?.({
                        message: NOT_AUTHORIZED_ERROR_MESSAGE,
                    });

                    break;
                default:
                    setGeneralNotification?.({
                        message: GENERIC_ERROR_MESSAGE,
                    });
                    break;
            }

            trackPasswordlessEmailFailed({
                errorMessage: errorCode,
                analytics: contexts.configContext?.analytics,
            });
        });
};

/**
 * Sends passworless login email
 */
export const sendVerificationEmailAction = ({
    email,
    contexts,
    target,
}: {
    email: string;
    contexts: IAllContextsProps;
    target: string;
}) => {
    trackVerificationEmailAttempt({
        socialProvider: GA_EVENTBRITE,
        analytics: contexts.configContext?.analytics,
    });
    const setGeneralNotification =
        contexts.notificationsContext?.methods.setGeneralNotification;
    const setVerificationEmailStatus =
        contexts.frontendContext?.methods.setVerificationEmailStatus;

    setGeneralNotification?.({});
    setVerificationEmailStatus?.(VERIFICATION_EMAIL_STATUSES.ongoing);
    contexts.userContext?.methods.setEmailVerificationStatus(
        VERIFICATION_STATUSES.empty,
    );

    return sendVerificationEmail({ email, target })
        .then(() => {
            setVerificationEmailStatus?.(VERIFICATION_EMAIL_STATUSES.delivered);
            trackVerificationEmailSent({
                socialProvider: GA_EVENTBRITE,
                analytics: contexts.configContext?.analytics,
            });
        })
        .catch((error: any) => {
            const errorCode =
                error?.parsedError?.error ??
                error?.response?.statusText ??
                'Error not defined';

            trackVerificationEmailFailed({
                errorMessage: errorCode,
                analytics: contexts.configContext?.analytics,
            });

            switch (errorCode) {
                case 'HIT_RATE_LIMIT':
                    setVerificationEmailStatus?.(
                        VERIFICATION_EMAIL_STATUSES.limitReached,
                    );
                    break;

                default:
                    setVerificationEmailStatus?.(
                        VERIFICATION_EMAIL_STATUSES.failed,
                    );
                    setGeneralNotification?.({
                        message: GENERIC_ERROR_MESSAGE,
                    });
                    contexts?.frontendContext?.methods?.setShowModal(
                        MODAL_TYPES.closed,
                    );
                    break;
            }
        });
};
